// src/components/Ownerdashboard/OwnerOtpVerify.jsx
import React, { useEffect, useRef, useState } from "react";
import { FaArrowLeft } from "react-icons/fa";
import { sendOwnerOtp, verifyOwnerOtp } from "./ownerAuth";

const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;
const MAX_RESEND = 5;

const OwnerOtpVerify = ({ number, onVerified, onChangeNumber }) => {
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(RESEND_SECONDS);
  const [resendCount, setResendCount] = useState(0);
  const inputsRef = useRef([]);

  const otp = digits.join("");

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (timer <= 0) return undefined;
    const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [timer]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const setDigitAt = (index, value) => {
    setDigits((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleChange = (index, rawValue) => {
    const value = rawValue.replace(/\D/g, "");
    if (error) setError("");

    if (!value) {
      setDigitAt(index, "");
      return;
    }

    // Typing (or an autofilled code) can deliver more than one digit at once.
    const chars = value.split("");
    setDigits((prev) => {
      const next = [...prev];
      chars.forEach((char, offset) => {
        if (index + offset < OTP_LENGTH) next[index + offset] = char;
      });
      return next;
    });

    const nextIndex = Math.min(index + chars.length, OTP_LENGTH - 1);
    inputsRef.current[nextIndex]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      e.preventDefault();
      setDigitAt(index - 1, "");
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      e.preventDefault();
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    e.preventDefault();

    const next = Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((char, i) => {
      next[i] = char;
    });
    setDigits(next);
    inputsRef.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");

    if (otp.length !== OTP_LENGTH) {
      setError(`Please enter the ${OTP_LENGTH}-digit OTP.`);
      return;
    }

    setLoading(true);
    try {
      const result = await verifyOwnerOtp(number, otp);

      if (result.success) {
        onVerified?.(result);
        return;
      }

      setError(result.message);
      setDigits(Array(OTP_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
    } catch (err) {
      setError("An unexpected error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCount >= MAX_RESEND) {
      setError(`You have reached the maximum number of resend attempts (${MAX_RESEND}).`);
      return;
    }

    setError("");
    setInfo("");
    setLoading(true);
    try {
      const result = await sendOwnerOtp(number);

      if (result.success) {
        setResendCount((prev) => prev + 1);
        setTimer(RESEND_SECONDS);
        setDigits(Array(OTP_LENGTH).fill(""));
        inputsRef.current[0]?.focus();
        setInfo(`OTP resent to +91 ${number}`);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError("Failed to resend OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <button
          type="button"
          onClick={onChangeNumber}
          className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-[#503691] hover:underline"
          disabled={loading}
        >
          <FaArrowLeft size={12} /> Change number
        </button>

        <p className="text-2xl font-bold">Verify OTP</p>
        <p className="mt-2 mb-6 text-gray-600">
          Enter the {OTP_LENGTH}-digit code sent to <b>+91 {number}</b>
        </p>

        <div className="mb-4 flex justify-between gap-2" onPaste={handlePaste}>
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                inputsRef.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              autoComplete={index === 0 ? "one-time-code" : "off"}
              maxLength={OTP_LENGTH}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onFocus={(e) => e.target.select()}
              className="h-12 w-full min-w-0 rounded-md border border-gray-300 text-center text-xl font-semibold text-gray-800 focus:border-[#503691] focus:outline-none focus:ring-1 focus:ring-[#503691] sm:h-14"
              disabled={loading}
            />
          ))}
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-center text-red-600 shadow-sm">
            <div className="flex items-center justify-center gap-2">
              <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="font-medium">{error}</span>
            </div>
          </div>
        )}

        {info && !error && (
          <p className="mb-4 text-center text-sm font-medium text-green-600">{info}</p>
        )}

        <button
          type="submit"
          className="h-11 w-full rounded-full bg-[#503691] font-semibold text-white hover:bg-[#5b54e6] disabled:cursor-not-allowed disabled:opacity-50"
          disabled={loading || otp.length !== OTP_LENGTH}
        >
          {loading ? "Verifying..." : "Verify OTP"}
        </button>

        <div className="mt-5 text-center">
          {timer > 0 ? (
            <p className="text-gray-600">
              Resend OTP in <span className="font-bold text-[#503691]">{formatTime(timer)}</span>
            </p>
          ) : resendCount < MAX_RESEND ? (
            <button
              type="button"
              onClick={handleResend}
              className="font-semibold text-[#503691] hover:underline disabled:opacity-50"
              disabled={loading}
            >
              Resend OTP {resendCount > 0 && `(${MAX_RESEND - resendCount} attempts left)`}
            </button>
          ) : (
            <p className="text-sm font-semibold text-red-500">Maximum resend attempts reached.</p>
          )}
        </div>
      </div>
    </form>
  );
};

export default OwnerOtpVerify;
